import { Button, Chip, Skeleton, Stack } from "@mui/material";
import Form from "@poseidon/ui/components/form/FullWidthForm";
import { Panel } from "@poseidon/ui/components/Panel";
import { ToggleButtonControl } from "@poseidon/ui/components/ToggleButtonControl";
import config from "@poseidon/utils/config";
import { Formik } from "formik";
import {
  BoolNullIcon,
  FormikDisabler,
  SummaryTable,
  SummaryTableRow,
  usePermissions,
} from "@poseidon/ui/components";
import { useIndustries } from "../../hooks/useIndustries";
import { useMemo } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingIcon from "@mui/icons-material/Pending";
import WarningIcon from "@mui/icons-material/Warning";

export const RiskLevel = ({ risk }) => {
  if (risk === "high" || risk === true) {
    return (
      <Chip
        size="small"
        icon={<CancelIcon />}
        color="error"
        label="High Risk"
      />
    );
  }
  if (risk === "medium") {
    return (
      <Chip
        size="small"
        icon={<WarningIcon />}
        color="warning"
        label="Medium Risk"
      />
    );
  }
  if (risk === "low" || risk === false) {
    return (
      <Chip
        size="small"
        icon={<CheckCircleIcon />}
        color="success"
        label="Low Risk"
      />
    );
  }
  return <Chip size="small" icon={<PendingIcon />} label={`Unknown Risk`} />;
};

export const KycDesignation = ({
  sailorRiskProfile,
  sailor,
  updateSailorRiskProfile,
}) => {
  const { canWrite } = usePermissions();
  const { riskOptions } = config;
  const { industries, loading: industriesLoading } = useIndustries(true);
  const industry = useMemo(() => {
    return industries?.find((i) => i.industry === sailor?.industrySector);
  }, [sailor?.industrySector, industries]);
  return (
    <Formik
      initialValues={{
        kycAmlStatus: sailorRiskProfile.kycAmlStatus ?? "pending",
      }}
      enableReinitialize
      onSubmit={updateSailorRiskProfile}
    >
      {({ dirty, isSubmitting }) => (
        <Form>
          <FormikDisabler disabled={!canWrite} />
          <Panel
            title="Designation"
            expandable
            actions={
              <Button
                size="small"
                type="submit"
                disabled={!dirty || isSubmitting || !canWrite}
              >
                Save
              </Button>
            }
          >
            <Stack spacing={2}>
              <ToggleButtonControl
                name="kycAmlStatus"
                label="KYC/AML Designation"
                options={riskOptions}
                helperText={
                  "This will be automatically set based on sailor personal details answers"
                }
              />
              <SummaryTable sx={{ alignSelf: "start", width: 500 }}>
                <SummaryTableRow label="Industry">
                  <Stack direction="row" spacing={1} alignItems="center">
                    {industriesLoading ? (
                      <Skeleton variant="text" />
                    ) : (
                      <>
                        <div>{industry?.industry ?? "Unknown"}</div>
                        <RiskLevel risk={industry?.risk} />
                      </>
                    )}
                  </Stack>
                </SummaryTableRow>
                <SummaryTableRow label="Dual National">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BoolNullIcon value={sailor.hasDualNationality} />
                    <div>{sailor.nationality}</div>
                    <RiskLevel risk={sailor.hasDualNationality} />
                  </Stack>
                </SummaryTableRow>
                <SummaryTableRow label="Other Tax Residency">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BoolNullIcon value={sailor.hasOtherTaxResidency} />
                    <div>{sailor.taxResidency}</div>
                    <RiskLevel risk={sailor.hasOtherTaxResidency} />
                  </Stack>
                </SummaryTableRow>
              </SummaryTable>
            </Stack>
          </Panel>
        </Form>
      )}
    </Formik>
  );
};
